import { useState, useEffect } from 'react';
import { getDefectStatuses } from 'services/clients/sites/siteApplications/defectStatuses';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { showError } from '../../../../../redux/common/actions';
import { getLocalStorageData } from 'helpers/utils';
import i18n from 'i18n';
import { SHOW_ALL_OPTION } from '../../constants';

export const useDefectStatus = (showAll = true) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [defectStatuses, setDefectStatuses] = useState([]);
	const userData = getLocalStorageData("me");
	const siteAppID = userData?.siteAppID;

	// Define group options once to avoid duplication.
	// `groupBy` stays the STABLE English identifier (matched against each
	// status's groupBy); `name` is translated for display only.
	const groupOptions = [
		{ id: "C", statusType: "C", groupBy: "All Complete", name: t("label.allComplete") },
		{ id: "O", statusType: "O", groupBy: "All Outstanding", name: t("label.allOutstanding") },
	];

	const buildStatusOptions = (processedData = []) => {
		return showAll 
			? [SHOW_ALL_OPTION, ...groupOptions, ...processedData]
			: [...groupOptions, ...processedData];
	};

	useEffect(() => {
		const loadDefectStatuses = async () => {
			try {
				const response = await getDefectStatuses(siteAppID);
				if (response?.status && response?.data) {
					const fetchedStatuses = response.data || [];
					
					// Add the grouped statuses based on type character
					const processedData = fetchedStatuses.map((status) => {
						let groupName;
						if (status.type === "C") {
							groupName = "All Complete";
						} else if (status.type === "O") {
							groupName = "All Outstanding";
						}
						
						return { ...status, groupBy: groupName };
					});
					
					setDefectStatuses(buildStatusOptions(processedData));
				} else {
					// Fallback for unsuccessful response
					setDefectStatuses(buildStatusOptions());
				}
			} catch (error) {
				console.error('Failed to fetch defect statuses:', error);
				// Set fallback data on error
				setDefectStatuses(buildStatusOptions());
				dispatch(
					showError(
						error?.response?.data?.detail || t('message.failedToFetchDefectStatuses')
					)
				);
			}
		};

		if (siteAppID) {
			loadDefectStatuses();
		} else {
			// Set default data if no siteAppID
			setDefectStatuses(buildStatusOptions());
		}
	}, [siteAppID, showAll, dispatch, t]);

	// Ensure we always return an array
	return Array.isArray(defectStatuses) ? defectStatuses : [];
};

// Export group members for external use.
// `groupBy` is the STABLE identifier matched against each status's `groupBy`
// value for grouping; `name` is translated for display only (getter defers
// i18n.t to access time so it is never called at module load).
export const GROUP_MEMBERS = [
	{ id: "C", statusType: "C", groupBy: "All Complete", get name() { return i18n.t("label.allComplete"); } },
	{ id: "O", statusType: "O", groupBy: "All Outstanding", get name() { return i18n.t("label.allOutstanding"); } },
];